'use client'

import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import '@/styles/bauhaus.css'
import { useLanguage } from '@/i18n/LanguageProvider'
import { appLanguageToContentLocale } from '@/i18n/contentLocale'
import chinaPoetryData from './china-poetry.json'

export default function ChinaPoetryEcology() {
  const { t, language } = useLanguage()
  const lang = appLanguageToContentLocale(language)

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1C1C1C] to-[#252525]">
      {/* 页面头部 */}
      <div className="pt-32 pb-16 relative bg-gradient-to-b from-[#1C1C1C] to-[#1C1C1C]">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="absolute w-full h-full bg-[radial-gradient(ellipse_at_center,_rgba(120,120,120,0.1)_0%,rgba(0,0,0,0)_70%)]"></div>
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          {/* 返回链接 */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <Link 
              to="/Qiaodong/BPF"
              className="inline-flex items-center text-[#B9AE9A]/60 hover:text-[#B9AE9A] transition-colors duration-300 text-sm font-bauhaus"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {t('bpf.back_to_qiaodong')}
            </Link>
          </motion.div>

          <div className="text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl font-bauhaus text-white tracking-tight mb-6"
            >
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#B9AE9A] to-[#8C4233]">
                {chinaPoetryData.title[lang]}
              </span>
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-lg text-white/70 max-w-4xl mx-auto leading-relaxed"
            >
              {chinaPoetryData.description[lang]}
            </motion.p>
          </div>
        </div>
      </div>

      {/* 主要内容 */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* 三个活动并排显示 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
          {/* 生活与洞见诗选展 */}
          <Link to="/Qiaodong/BPF/china-poetry/collective">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="bg-[#1C1C1C]/40 backdrop-blur-sm border border-[#B9AE9A]/20 rounded-xl p-6 group hover:border-[#B9AE9A]/40 transition-all duration-300 hover:transform hover:scale-[1.02] cursor-pointer"
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-[#B9AE9A] to-[#8C4233] rounded-lg flex items-center justify-center mx-auto mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                
                <h3 className="text-xl font-bauhaus text-white group-hover:text-[#B9AE9A] transition-colors duration-300">
                  {chinaPoetryData.sections.collective.title[lang]}
                </h3>
              </div>
            </motion.div>
          </Link>

          {/* 中国摇滚乐与诗歌 */}
          <Link to="/Qiaodong/BPF/china-poetry/rock-music">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="bg-[#1C1C1C]/40 backdrop-blur-sm border border-[#B9AE9A]/20 rounded-xl p-6 group hover:border-[#B9AE9A]/40 transition-all duration-300 hover:transform hover:scale-[1.02] cursor-pointer"
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-[#506B72] to-[#B9AE9A] rounded-lg flex items-center justify-center mx-auto mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                  </svg>
                </div>
                
                <h3 className="text-xl font-bauhaus text-white group-hover:text-[#B9AE9A] transition-colors duration-300">
                  {chinaPoetryData.sections.rock_music.title[lang]}
                </h3>
              </div>
            </motion.div>
          </Link>

          {/* 月与潮汐 */}
          <Link to="/Qiaodong/BPF/china-poetry/moon-tides">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="bg-[#1C1C1C]/40 backdrop-blur-sm border border-[#B9AE9A]/20 rounded-xl p-6 group hover:border-[#B9AE9A]/40 transition-all duration-300 hover:transform hover:scale-[1.02] cursor-pointer"
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-[#8C4233] to-[#506B72] rounded-lg flex items-center justify-center mx-auto mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                </div>
                
                <h3 className="text-xl font-bauhaus text-white group-hover:text-[#B9AE9A] transition-colors duration-300">
                  {chinaPoetryData.sections.moon_tides.title[lang]}
                </h3>
              </div>
            </motion.div>
          </Link>
        </div>

        {/* 回到主页链接 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="text-center mt-8"
        >
          <Link 
            to="/Qiaodong/BPF"
            className="inline-flex items-center text-[#B9AE9A]/60 hover:text-[#B9AE9A] transition-colors duration-300 text-sm font-bauhaus"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            {t('bpf.back_to_qiaodong')}
          </Link>
        </motion.div>
      </div>
    </div>
  )
} 